import type { ColumnFilters } from "@/components/MaximusTableReusable";
import { formatDateOnlyLocal } from "@/lib/utils";

/** A single server-side sort instruction. */
export interface SortInput {
  field: string;
  order: "Ascending" | "Descending";
}

/** Relay-style connection returned by the GraphQL list endpoints. */
export interface Connection<T> {
  edges: { cursor: string; node: T }[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
}

/**
 * Which column keys are treated as which filter kind when translating the
 * table's visible filters into an API filter input.
 */
export interface FilterFieldSets {
  text?: Set<string>;
  date?: Set<string>;
  number?: Set<string>;
  multiSelect?: Set<string>;
}

const EMPTY_FIELDS = new Set<string>();

/**
 * Translate the table's visible column filters into the GraphQL filter input
 * expected by the backing view. Returns undefined when nothing is filtered.
 */
export function buildApiFilter(
  columnFilters: ColumnFilters,
  fields: FilterFieldSets,
): Record<string, unknown> | undefined {
  const entries = Object.entries(columnFilters);
  if (entries.length === 0) return undefined;

  const textFields = fields.text ?? EMPTY_FIELDS;
  const dateFields = fields.date ?? EMPTY_FIELDS;
  const numberFields = fields.number ?? EMPTY_FIELDS;
  const multiSelectFields = fields.multiSelect ?? EMPTY_FIELDS;

  const filterInput: Record<string, unknown> = {};

  for (const [columnKey, filterValue] of entries) {
    if (multiSelectFields.has(columnKey)) {
      if (filterValue.mode === "isEmpty") {
        filterInput[columnKey] = { isBlank: true };
      } else if (
        Array.isArray(filterValue.value) &&
        filterValue.value.length > 0
      ) {
        filterInput[columnKey] = {
          containsValues: filterValue.value.map((v) => String(v)),
        };
      }
    } else if (textFields.has(columnKey)) {
      if (filterValue.mode === "isEmpty") {
        filterInput[columnKey] = { isBlank: true };
      } else if (filterValue.value != null && filterValue.value !== "") {
        const strValue = String(filterValue.value);
        filterInput[columnKey] =
          filterValue.mode === "equals"
            ? { eq: strValue }
            : { contains: strValue };
      }
    } else if (dateFields.has(columnKey)) {
      const dateFilter: Record<string, string> = {};
      if (filterValue.dateFrom) {
        dateFilter.ge = formatDateOnlyLocal(filterValue.dateFrom);
      }
      if (filterValue.dateTo) {
        dateFilter.le = formatDateOnlyLocal(filterValue.dateTo);
      }
      if (Object.keys(dateFilter).length > 0) {
        filterInput[columnKey] = dateFilter;
      }
    } else if (numberFields.has(columnKey)) {
      const numFilter: Record<string, number> = {};
      if (filterValue.numberFrom != null) {
        numFilter.ge = filterValue.numberFrom;
      }
      if (filterValue.numberTo != null) {
        numFilter.le = filterValue.numberTo;
      }
      if (Object.keys(numFilter).length > 0) {
        filterInput[columnKey] = numFilter;
      }
    }
  }

  return Object.keys(filterInput).length > 0 ? filterInput : undefined;
}
